// Tracks the URL the user originally tried to reach when they got bounced to
// the login page (deep links, 401 redirects, manual router.replace('/login'),
// etc.) so we can send them back there after a successful sign-in — both for
// email/password and Guards SSO logins.

const STORAGE_KEY = 'dm_post_login_redirect';

// Paths that must never be used as a post-login redirect target — landing on
// any of them after login would either loop back to login or to a public-only
// page that doesn't make sense once authenticated.
const NON_RETURNABLE_PATHS = [
  '/login',
  '/account-registration',
  '/space-join',
  '/account-password-update',
  '/account-password-reset',
  '/forgot-password',
  '/change-password',
  '/update-account',
];

function isReturnable(path) {
  if (!path || typeof path !== 'string') return false;
  // Must be a same-origin relative path. Reject absolute URLs, protocol-relative
  // URLs, and anything that could be used as an open-redirect vector.
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  return !NON_RETURNABLE_PATHS.some(p => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`));
}

// Router uses hash mode in development and history mode in production
// (see src/router/index.js). For full-page navigation we need to embed the
// path under the hash so the route resolves correctly in dev.
function isHashMode() {
  return process.env.NODE_ENV !== 'production';
}

function buildFullUrl(target) {
  if (isHashMode()) {
    return `${window.location.origin}/#${target}`;
  }
  return target;
}

export const postLoginRedirect = {
  // Save the given path (or current location if not provided) as the intended
  // destination. Safe to call from router guards — pass `to.fullPath` for
  // accurate capture before navigation has actually happened.
  capture(path) {
    try {
      let target;
      if (typeof path === 'string' && path.length > 0) {
        target = path;
      } else if (isHashMode() && window.location.hash) {
        // In hash mode the "real" route lives inside the hash. Extract it so we
        // store something like `/devices/...`, never `/#/devices/...`.
        target = window.location.hash.replace(/^#/, '') || '/';
      } else {
        target = window.location.pathname + window.location.search + window.location.hash;
      }
      if (isReturnable(target)) {
        sessionStorage.setItem(STORAGE_KEY, target);
      }
    } catch (_) {
      // sessionStorage can throw in private mode; failing to capture just means
      // the user lands on '/' after login, which is acceptable.
    }
  },

  // Returns and clears the captured path, or '/' if none/invalid.
  consume() {
    let target = '/';
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
      if (isReturnable(stored)) target = stored;
    } catch (_) {
      // ignore
    }
    return target;
  },

  // Same as consume() but returns a fully qualified URL suitable for
  // window.location.href / window.location.replace, accounting for hash vs
  // history routing mode.
  consumeAsUrl() {
    return buildFullUrl(this.consume());
  },

  // Navigate to the captured target with a guaranteed full page reload so the
  // root Vue instance (App.vue) remounts and re-runs initialisation — user
  // fetch, websocket subscription, refresh-token interval, etc.
  //
  // We must defend against two browser quirks:
  //  1. In hash mode, changing only the hash does not trigger a reload.
  //  2. After a Keycloak redirect the current URL still contains stale
  //     `?code=…&session_state=…` and possibly a stale `#/login` fragment.
  //     Using `window.location.replace` with a hash-only target can leave
  //     those leftovers visible (e.g. `/#/devices#/login`).
  //
  // To avoid both, we explicitly rewrite the URL through the History API
  // (clearing any leftover search/hash from Keycloak), then force a reload.
  navigateAndReload() {
    const url = buildFullUrl(this.consume());
    try {
      // Replace the entry so the Keycloak callback URL is not in history.
      window.history.replaceState(null, '', url);
    } catch (_) {
      // Older browsers / sandboxed contexts — fall back to direct assignment.
      window.location.href = url;
    }
    // Always force a real reload so App.vue remounts.
    window.location.reload();
  },

  clear() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      // ignore
    }
  },
};
